import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { Toaster } from "sonner";
import RoleGate from "@/components/RoleGate";

import Home from "@/pages/Home";
import HowItWorks from "@/pages/HowItWorks";
import FAQ from "@/pages/FAQ";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import About from "@/pages/About";
import WhyChoose from "@/pages/WhyChoose";
import Pricing from "@/pages/Pricing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import NewRequest from "@/pages/NewRequest";
import RequestDetail from "@/pages/RequestDetail";
import Orders from "@/pages/Orders";
import Requests from "@/pages/Requests";
import OrderDetail from "@/pages/OrderDetail";
import Support from "@/pages/Support";
import Account from "@/pages/Account";
import SavedICPs from "@/pages/SavedICPs";
import SharedSample from "@/pages/SharedSample";
import Team from "@/pages/Team";
import AcceptInvite from "@/pages/AcceptInvite";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminRequests from "@/pages/admin/AdminRequests";
import AdminRequestDetail from "@/pages/admin/AdminRequestDetail";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminOrderDetail, { AdminOrdersList } from "@/pages/admin/AdminOrder";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminDiscounts from "@/pages/admin/AdminDiscounts";
import AdminTemplates from "@/pages/admin/AdminTemplates";
import CookieBanner from "@/components/CookieBanner";
import ScrollToTop from "@/components/ScrollToTop";

const STAFF_ROLES = ["admin", "analyst", "quality", "finance"];
const ADMIN_ONLY = ["admin"];
const ADMIN_FINANCE = ["admin", "finance"];

function Router() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/legal/:slug" element={<Legal />} />
      <Route path="/about" element={<About />} />
      <Route path="/why-choose-us" element={<WhyChoose />} />
      <Route path="/share/sample/:token" element={<SharedSample />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />

      <Route path="/dashboard" element={<RoleGate><Dashboard /></RoleGate>} />
      <Route path="/new-request" element={<RoleGate><NewRequest /></RoleGate>} />
      <Route path="/requests" element={<RoleGate><Requests /></RoleGate>} />
      <Route path="/requests/:id" element={<RoleGate><RequestDetail /></RoleGate>} />
      <Route path="/orders" element={<RoleGate><Orders /></RoleGate>} />
      <Route path="/orders/:id" element={<RoleGate><OrderDetail /></RoleGate>} />
      <Route path="/support" element={<RoleGate><Support /></RoleGate>} />
      <Route path="/account" element={<RoleGate><Account /></RoleGate>} />
      <Route path="/icps" element={<RoleGate><SavedICPs /></RoleGate>} />
      <Route path="/team" element={<RoleGate><Team /></RoleGate>} />
      <Route path="/accept-invite" element={<AcceptInvite />} />

      <Route path="/admin" element={<RoleGate allow={STAFF_ROLES}><AdminDashboard /></RoleGate>} />
      <Route path="/admin/requests" element={<RoleGate allow={STAFF_ROLES}><AdminRequests /></RoleGate>} />
      <Route path="/admin/requests/:id" element={<RoleGate allow={STAFF_ROLES}><AdminRequestDetail /></RoleGate>} />
      <Route path="/admin/orders" element={<RoleGate allow={STAFF_ROLES}><AdminOrdersList /></RoleGate>} />
      <Route path="/admin/orders/:id" element={<RoleGate allow={STAFF_ROLES}><AdminOrderDetail /></RoleGate>} />
      <Route path="/admin/users" element={<RoleGate allow={ADMIN_ONLY}><AdminUsers /></RoleGate>} />
      <Route path="/admin/settings" element={<RoleGate allow={ADMIN_ONLY}><AdminSettings /></RoleGate>} />
      <Route path="/admin/analytics" element={<RoleGate allow={STAFF_ROLES}><AdminAnalytics /></RoleGate>} />
      <Route path="/admin/discounts" element={<RoleGate allow={ADMIN_FINANCE}><AdminDiscounts /></RoleGate>} />
      <Route path="/admin/templates" element={<RoleGate allow={ADMIN_ONLY}><AdminTemplates /></RoleGate>} />

      <Route path="*" element={<Home />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Router />
          <CookieBanner />
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
